import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { setMallScope } from '@/lib/api/client';

/**
 * AVM KAPSAMI — çoklu AVM sisteminin en kritik durumu.
 *
 * NEDEN AYRI BİR STORE: Kullanıcının hangi AVM'nin verisine baktığı, tek tek
 * ekranların değil ALTYAPININ sorumluluğudur. Bu değer:
 *   1. HTTP interceptor tarafından `X-Mall-Id` başlığına otomatik eklenir,
 *   2. `useScopedQuery` tarafından HER sorgu anahtarına zorla iliştirilir.
 *
 * Bu iki mekanizma olmadan, iki yıllık bir inşada bir yerde `mallId` unutulur
 * ve kullanıcı başka bir AVM'nin verisini görür. Müşteriye bu, API doğru
 * çalışsa bile bir VERİ GÜVENLİĞİ İHLALİ gibi görünür.
 */

/** Portföy (tüm AVM'ler) modu için özel değer. */
export const ALL_MALLS = 'ALL' as const;

export type MallScopeValue = string | typeof ALL_MALLS;

export interface MallSummary {
  id: string;
  name: string;
  city: string;
  /** Kısa kod — avatar/rozet gösteriminde kullanılır. */
  code: string;
  gla: number;
  unitCount: number;
}

interface MallScopeState {
  mallId: MallScopeValue;
  availableMalls: MallSummary[];
  /** Portföy modunu görme yetkisi (permission: mall.viewAll). */
  canViewAll: boolean;
  /** Son kullanılan AVM'ler — seçicide en üstte gösterilir. */
  recentMallIds: string[];
}

interface MallScopeActions {
  setMall: (id: MallScopeValue) => void;
  /**
   * Profil yanıtından kapsamı kurar.
   *
   * AVM listesi ve portföy yetkisi TEK BİR EYLEMDE ayarlanır; ayrı ayrı
   * değil. Ayrı olduklarında aralarında geçersiz bir ara durum oluşuyordu:
   * liste tek AVM'ye inmiş ama `canViewAll` hâlâ varsayılan `true` iken
   * seçici "Tüm AVM'ler" gösteriyor, istekler ise `X-Mall-Id` başlığı
   * olmadan gidip 400 alıyordu.
   */
  syncFromProfile: (malls: MallSummary[], canViewAll: boolean) => void;
}

export const useMallScopeStore = create<MallScopeState & MallScopeActions>()(
  persist(
    (set, get) => ({
      /*
        VARSAYILAN "TÜM AVM'LER" DEĞİLDİR.

        Portföy modu bir AYRICALIKTIR ve çoğu kullanıcıda yoktur. Varsayılanı
        portföy yapmak, yetkisiz kullanıcıyı açılışta hatalı bir kapsamda
        bırakır. Gerçek değer profil geldiğinde `syncFromProfile` ile kurulur;
        buradaki boş dize "henüz bilinmiyor" demektir.
      */
      mallId: '',
      availableMalls: [],
      canViewAll: false,
      recentMallIds: [],

      setMall: (id) => {
        const { recentMallIds } = get();

        // Portföy modu "son kullanılanlar" listesine girmez —
        // orası belirli AVM'lere hızlı dönüş içindir.
        const nextRecent =
          id === ALL_MALLS
            ? recentMallIds
            : [id, ...recentMallIds.filter((r) => r !== id)].slice(0, 3);

        set({ mallId: id, recentMallIds: nextRecent });
      },

      syncFromProfile: (malls, canViewAll) => {
        const { mallId } = get();

        /*
          SEÇİLİ KAPSAM HER ZAMAN DOĞRULANIR.

          Kaydedilmiş kapsam (localStorage) eskimiş olabilir: kullanıcının
          yetkisi alınmış, AVM silinmiş ya da demo veri yeniden yüklenmiş
          olabilir. Doğrulamadan kullanmak, erişilemeyen bir AVM kimliğiyle
          istek atmak ve her ekranda 403 görmek demektir.
        */
        const isValid =
          mallId === ALL_MALLS ? canViewAll : malls.some((m) => m.id === mallId);

        // Geçersizse: portföy yetkisi varsa portföy, yoksa ilk AVM.
        const fallback: MallScopeValue = canViewAll ? ALL_MALLS : (malls[0]?.id ?? '');

        set({
          availableMalls: malls,
          canViewAll,
          mallId: isValid ? mallId : fallback,
        });
      },
    }),
    {
      name: 'mallos.mallScope',
      // Sunucudan gelen AVM listesi kalıcı olmamalı: yetkiler değişmiş
      // olabilir ve eski liste yanıltıcı olur. Her oturumda yeniden alınır.
      partialize: ({ mallId, recentMallIds }) => ({ mallId, recentMallIds }),
    },
  ),
);

/** Seçili AVM nesnesi (portföy modunda `null`). */
export function useActiveMall(): MallSummary | null {
  const mallId = useMallScopeStore((s) => s.mallId);
  const malls = useMallScopeStore((s) => s.availableMalls);

  if (mallId === ALL_MALLS) return null;
  return malls.find((m) => m.id === mallId) ?? null;
}

export function useIsPortfolioMode(): boolean {
  return useMallScopeStore((s) => s.mallId) === ALL_MALLS;
}

/*
  =============================================================================
  KAPSAM → HTTP BAŞLIĞI KÖPRÜSÜ
  =============================================================================

  BU KÖPRÜ BİR EFEKT DEĞİL, BİR ABONELİKTİR — VE BUNUN NEDENİ SOMUT.

  Önce `App` içinde bir `useEffect` ile kuruluyordu:

      useEffect(() => { setMallScope(mallId); }, [mallId]);

  Çalışmadı. React'te efektler AŞAĞIDAN YUKARI çalışır: çocuk bileşenlerin
  efektleri ebeveynin efektinden ÖNCE tetiklenir. Kabuktaki bildirim zili,
  App henüz kapsamı kurmadan isteğini atıyor ve o istek `X-Mall-Id` başlığı
  olmadan gidiyordu. Sunucu 400 dönüyor, React Query 4xx'i yeniden
  denemediği için zil kalıcı olarak boş kalıyordu.

  Abonelik render döngüsünün tamamen dışındadır: `mallId` değiştiği ANDA
  başlık güncellenir, hangi bileşenin ne zaman render olduğundan bağımsız
  olarak. Ayrıca modül yüklenirken bir kez uygulanır ki, kaydedilmiş
  (localStorage) kapsam ilk istekten önce yerini alsın.

  YÖN KURALI: store → client (tek yön). `lib/api/client` hiçbir store'u
  import etmez; aksi halde döngüsel bağımlılık oluşurdu.
*/
function pushScopeToClient(mallId: MallScopeValue): void {
  setMallScope(mallId === ALL_MALLS || mallId === '' ? null : mallId);
}

pushScopeToClient(useMallScopeStore.getState().mallId);

useMallScopeStore.subscribe((state, previous) => {
  if (state.mallId !== previous.mallId) pushScopeToClient(state.mallId);
});
